import type { AnyObject } from '@/common';
import type { IHttpParams } from '@/common/hooks/useHttp';
import { useCallback, useEffect, useMemo } from 'react';
import get from 'lodash/get';
import set from 'lodash/set';
import { Toaster } from 'sonner';

import type { GetUIElementByType } from '@ballerine/common';

import type { ITask } from '../../providers/TaskRunner/types';
import type { TDynamicFormField } from '../../types';
import type { IFieldListParams } from '../FieldList';
import type { IEntity } from './types';
import { Button } from '@/components/atoms';
import { useCreateDocumentMutation } from '../../../DocumentsService';
import { useReuploadDocumentMutation } from '../../../DocumentsService/domains/documents/mutations/useReuploadDocumentMutation';
import { useDocumentsService } from '../../../DocumentsService/hooks/internal/useDocumentsService';
import { useDynamicForm } from '../../context';
import { useElement, useField } from '../../hooks/external';
import { useFormHttp } from '../../hooks/internal/useFormHttp/useFormHttp';
import { useMountEvent } from '../../hooks/internal/useMountEvent';
import { useUnmountEvent } from '../../hooks/internal/useUnmountEvent';
import { FieldDescription } from '../../layouts/FieldDescription';
import { FieldErrors } from '../../layouts/FieldErrors';
import { FieldPriorityReason } from '../../layouts/FieldPriorityReason';
import { useTaskRunner } from '../../providers/TaskRunner/hooks/useTaskRunner';
import { useStack } from '../FieldList';
import { EntityFieldGroupDocument } from './components/EntityFieldGroupDocument';
import { EntityFields } from './components/EntityFields';
import { buildDocumentsCreationPayload } from './components/EntityFields/helpers/build-documents-creation-payload';
import { buildEntityCreationPayload } from './components/EntityFields/helpers/build-entity-for-creation';
import { buildEntityUpdatePayload } from './components/EntityFields/helpers/build-entity-for-update';
import { updateEntities } from './components/EntityFields/helpers/update-entities';
import { getEntityGroupValueDestination } from './helpers/get-entity-group-value-destination';
import { useEntityFieldGroupList } from './hooks/useEntityFieldGroupList';
import { EntityFieldProvider } from './providers/EntityFieldProvider';

// Entity types supported by entityfieldgroup element (for array-based entity collections)
export type TEntityFieldGroupType = 'director' | 'ubo' | 'trustee' | 'member' | 'partner';

export type ICreateEntityParams = {
  httpParams: IHttpParams;
  transform?: string;
};

export type IUpdateEntityParams = {
  httpParams: IHttpParams;
  transform?: string;
};

export type IEntityFieldGroupParams = {
  httpParams: {
    createEntity: ICreateEntityParams;
    deleteEntity: IHttpParams;
    uploadDocument: IHttpParams;
    updateEntity: IUpdateEntityParams;
    deleteDocument: IHttpParams;
  };
  createEntityText?: string;
  type: TEntityFieldGroupType;
} & IFieldListParams;

export const EntityFieldGroup: TDynamicFormField<GetUIElementByType<'entityfieldgroup'>> = ({
  element: _element,
}) => {
  const element = useMemo(
    () => ({
      ..._element,
      valueDestination: getEntityGroupValueDestination(_element.params?.type),
    }),
    [_element],
  );

  useMountEvent(element);
  useUnmountEvent(element);

  const { files, documents } = useDocumentsService();
  const { elementsMap } = useDynamicForm();
  const { stack } = useStack();
  const { id: fieldId, hidden } = useElement(element, stack);
  const { disabled, value, onChange } = useField<IEntity[]>(element, stack);
  const {
    addButtonLabel = 'Add Item',
    removeButtonLabel = 'Remove',
    itemIndexLabel = 'Item {INDEX}',
  } = element.params || {};
  const { items, isRemovingEntity, addItem, removeItem } = useEntityFieldGroupList({ element });
  const { run: createEntity, isLoading: isCreatingEntity } = useFormHttp(
    element.params.httpParams?.createEntity.httpParams,
  );
  const { run: updateEntity, isLoading: isUpdatingEntity } = useFormHttp(
    element.params.httpParams?.updateEntity.httpParams,
  );

  const { mutateAsync: createDocument } = useCreateDocumentMutation();
  const { mutateAsync: reuploadDocument } = useReuploadDocumentMutation();
  const { addTask, removeTask } = useTaskRunner();
  const elementsOverride = useMemo(
    () => ({
      ...elementsMap,
      documentfield: EntityFieldGroupDocument,
    }),
    [elementsMap],
  );

  const createEntitiesCreationTaskOnChange = useCallback(async () => {
    const TASK_ID = element.id;
    removeTask(TASK_ID);

    try {
      const taskRun = async (context: AnyObject) => {
        const entities = get(context, element.valueDestination, []) as IEntity[];

        const entitiesToProcess = await Promise.all(
          entities.map(entity =>
            entity.ballerineEntityId
              ? buildEntityUpdatePayload(element, entity, context)
              : buildEntityCreationPayload(element, entity, context),
          ),
        );
        const createdEntitiesIds: string[] = await Promise.all(
          entitiesToProcess.map(entityToProcess =>
            entityToProcess.ballerineEntityId
              ? updateEntity(entityToProcess.entity, {
                  params: { entityId: entityToProcess.ballerineEntityId },
                })
              : createEntity(entityToProcess.entity),
          ),
        );

        const documentsCreationPayload = buildDocumentsCreationPayload({
          element,
          entities: entities.map((entity, index) => {
            return {
              ...entity,
              ballerineEntityId: createdEntitiesIds[index]!,
            };
          }) as IEntity[],
          files: files.files,
        });

        await Promise.all(
          documentsCreationPayload.map(async ({ documentData, file, entity }) => {
            const document = documents.find(
              document =>
                document.endUserId === documentData.entityId &&
                document.type === documentData.type &&
                document.category === documentData.category,
            );

            const isDocumentExists = Boolean(document);

            const uploadedDocument = !isDocumentExists
              ? await createDocument({
                  data: documentData,
                  documentFile: file,
                })
              : await reuploadDocument({
                  documentId: document!.id,
                  documentFile: file,
                  metadata: {
                    documentType: documentData.documentType,
                    documentVariant: documentData.documentVariant,
                    pageIndex: documentData.documentPage,
                  },
                });

            if (entity.__id) {
              files.removeFile({
                type: documentData.documentType,
                category: documentData.documentVariant,
                entityType: 'ubo',
                entityId: entity.__id,
              });

              files.setFile(
                {
                  type: documentData.type,
                  category: documentData.category,
                  entityType: 'ubo',
                  entityId: entity.ballerineEntityId!,
                },
                file,
              );
            }

            return uploadedDocument;
          }),
        );

        const updatedEntities = updateEntities(entities, createdEntitiesIds);
        set(context, element.valueDestination, updatedEntities);

        onChange(updatedEntities);

        return context;
      };

      const task: ITask = {
        id: TASK_ID,
        element,
        run: taskRun,
      };

      addTask(task);
    } catch (error) {
      console.error(error);
    }
  }, [
    onChange,
    element,
    createEntity,
    stack,
    removeTask,
    addTask,
    updateEntity,
    files.files,
    files.setFile,
    files.removeFile,
    documents,
  ]);

  useEffect(() => {
    void createEntitiesCreationTaskOnChange();

    return () => {
      removeTask(element.id);
    };
  }, [value, documents, files.files, createEntitiesCreationTaskOnChange]);

  if (hidden) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4" data-testid={`${fieldId}-fieldlist`}>
      {items?.map((entity: IEntity, index: number) => {
        return (
          <EntityFieldProvider
            key={entity.__id || entity.ballerineEntityId}
            entityId={entity.ballerineEntityId}
            tempEntityId={entity.__id!}
            entityFieldGroupType={element.params?.type}
            isSyncing={isCreatingEntity || isUpdatingEntity}
            element={element}
          >
            <div className="flex flex-col gap-4">
              <div className="flex flex-row items-center justify-between">
                <span className="text-sm font-bold">
                  {itemIndexLabel.replace('{INDEX}', (index + 1).toString())}
                </span>
                <button
                  tabIndex={0}
                  aria-disabled={isRemovingEntity || disabled}
                  className="text-sm font-bold disabled:opacity-50"
                  disabled={isRemovingEntity || disabled}
                  data-testid={`${fieldId}-fieldlist-item-remove-${entity.__id}`}
                  onClick={isRemovingEntity ? undefined : () => removeItem(entity.__id!)}
                >
                  {removeButtonLabel}
                </button>
              </div>
              <EntityFields
                entityId={entity.__id!}
                index={index}
                stack={stack}
                fieldId={fieldId}
                element={element}
                elementsOverride={elementsOverride as AnyObject}
              />
            </div>
          </EntityFieldProvider>
        );
      })}
      <div className="flex flex-row justify-start">
        <Button
          onClick={addItem}
          disabled={disabled}
          className="border border-gray-200 bg-white text-[hsl(var(--muted-foreground))] shadow-[0_1px_2px_0_rgb(0_0_0_/_0.05)] hover:bg-gray-50 hover:shadow-[0_1px_2px_0_rgb(0_0_0_/_0.1)]"
        >
          {addButtonLabel}
        </Button>
      </div>
      <FieldDescription element={element} />
      <FieldPriorityReason element={element} />
      <FieldErrors element={element} />
      <Toaster richColors />
    </div>
  );
};
